import {
  FormProviderProps,
  type FieldValues,
  type Path,
  FormProvider as RHFFormProvider
} from 'react-hook-form';
import { useEffect, useRef } from 'react';
import { useTranslation } from '../../i18n';
import { DevTool } from '@hookform/devtools';

export const FormProvider = <T extends FieldValues>(
  props: FormProviderProps<T>
) => {
  const {
    control,
    formState: { errors },
    trigger
  } = props;
  const { version } = useTranslation();
  const previousVersionRef = useRef(version);
  useEffect(() => {
    if (previousVersionRef.current === version) return;
    previousVersionRef.current = version;
    // Revalidate only visible client errors. Keep API errors and untouched fields.
    const clientFields = (value: object, prefix = ''): string[] =>
      Object.entries(value).flatMap(([key, error]) => {
        if (key === 'root' || !error || typeof error !== 'object') return [];
        const path = prefix ? `${prefix}.${key}` : key;
        if ('type' in error)
          return error.type !== 'manual' && error.type !== 'server'
            ? [path]
            : [];
        return clientFields(error, path);
      });
    const fields = clientFields(errors) as Path<T>[];
    if (fields.length) void trigger(fields);
  }, [version, errors, trigger]);

  return (
    <>
      {process.env.NODE_ENV === 'development' && (
        <DevTool
          control={control}
          placement={'top-left'}
          styles={{
            button: {
              width: '20px'
            }
          }}
        />
      )}
      <RHFFormProvider {...props} />
    </>
  );
};
